/**
 * Labels of the product ("pricing and availability") Excel export,
 * `./ProductsExportUtils.tsx`, in en / fr / es. The column keys must match the field names of
 * `ExcelProductDetails` in that file, which looks each header up by field name. `noteText` and
 * `noteLinkTooltip` carry a `%company%` placeholder filled with the brand name. `energySurcharge`
 * has no reader.
 */
import { LanguageContent } from "@ryerson/frontend.common";

/** Label keys of the product export: metadata labels, column headers and cell words. */
export interface ProductsContentProps {
    customerNumber: string;
    totalOrderWeight: string;
    subTotal: string;
    orderSpecialCharges: string;
    itemSpecialCharges: string;
    totalExtendedPrice: string;
    taxes: string;
    fuelSurcharge: string;
    energySurcharge: string;
    discountApplied: string;
    customerPickup: string;
    currency: string;
    totalNetPrice: string;
    timeStamp: string;
    purchaseOrderNumber: string;
    note: string;
    noteText: string;
    noteLinkTooltip: string;
    partNumber: string;
    altType: string;
    alt: string;
    description: string;
    requestedQuantity: string;
    requestedUOM: string;
    cutDescription: string;
    cut: string;
    cutDimension1: string;
    cutDimension2: string;
    cutShape: string;
    process: string;
    tolerance1: string;
    tolerance2: string;
    totalStock: string;
    quantityUOM: string;
    deliveryDate: string;
    weight: string;
    netPrice: string;
    priceUOM: string;
    lineLevelPO: string;
    extendedPrice: string;
    requestFutureDeliveryDate: string;
    customerReferenceNumber: string;
    lineItemNote: string;
    domesticRequested: string;
    tbd: string;
    seeAbove: string;
    cutFrom: string;
}

/** Product export labels by language. */
export const ExcelExportContent: LanguageContent<ProductsContentProps> = {
    en: {
        customerNumber: "Customer Number",
        totalOrderWeight: "Total Order Weight (LB)",
        subTotal: "Subtotal (Est.)",
        orderSpecialCharges: "Order Special Charges",
        itemSpecialCharges: "Item Special Charges",
        totalExtendedPrice: "Total Extended Price",
        taxes: "Taxes (Est.)",
        fuelSurcharge: "Fuel Surcharge (Est.)",
        energySurcharge: "Energy Surcharge (Est.)",
        discountApplied: "Discount Applied",
        customerPickup: "Customer Pickup",
        currency: "Currency",
        totalNetPrice: "Total Net Price (Est.)",
        timeStamp: "Timestamp",
        purchaseOrderNumber: "Purchase Order Number",
        note: "Note",
        noteText:
            "The %company% Conditions and Terms of Sale are applicable to all sales by %company%.",
        noteLinkTooltip: "%company% Conditions and Terms of Sale",
        partNumber: "Part Number",
        altType: "Alt Type",
        alt: "Alt",
        description: "Description",
        requestedQuantity: "Requested Quantity",
        requestedUOM: "Requested UOM",
        cutDescription: "Cut Description",
        cut: "Cut",
        cutDimension1: "Cut Dimension 1",
        cutDimension2: "Cut Dimension 2",
        cutShape: "Cut Shape",
        process: "Process",
        tolerance1: "Tolerance 1",
        tolerance2: "Tolerance 2",
        totalStock: "Total Stock",
        quantityUOM: "Quantity UOM",
        deliveryDate: "Delivery Date",
        weight: "Weight (LB)",
        netPrice: "Net Price",
        priceUOM: "Price UOM",
        extendedPrice: "Extended Price",
        lineLevelPO: "Line Level PO",
        requestFutureDeliveryDate: "Request Future Delivery Date",
        customerReferenceNumber: "Customer Reference Number",
        lineItemNote: "Line Item Note",
        domesticRequested: "Domestic Requested",
        tbd: "TBD",
        seeAbove: "See above",
        cutFrom: "Cut From: ",
    },
    fr: {
        customerNumber: "Numéro de client",
        totalOrderWeight: "Poids total de la commande (LB)",
        subTotal: "Total (Est.)",
        orderSpecialCharges: "Frais spéciaux pour la commande",
        itemSpecialCharges: "Frais spéciaux pour l'article",
        totalExtendedPrice: "Total Prix calculé",
        taxes: "Taxes (Est.)",
        fuelSurcharge: "Supplément carburant (Est.)",
        energySurcharge: "Supplément d'énergie (Est.)",
        discountApplied: "Remise appliquée",
        customerPickup: "Ramassage client",
        currency: "Devise",
        totalNetPrice: "Prix ​​net total (Est.)",
        timeStamp: "Horodatage",
        purchaseOrderNumber: "Numéro de commande",
        note: "Note",
        noteText:
            "Les conditions et conditions de vente de %company% s'appliquent à toutes les ventes réalisées par %company%.",
        noteLinkTooltip: "Conditions et modalités de vente de %company%",
        partNumber: "Numéro d'article",
        altType: "Type alternatif",
        alt: "Alt",
        description: "Description",
        requestedQuantity: "Quantité demandée",
        requestedUOM: "Demandée UOM",
        cutDescription: "Description de la coupe",
        cut: "Couper",
        cutDimension1: "Cote de coupe 1",
        cutDimension2: "Cote de coupe 2",
        cutShape: "Forme coupée",
        process: "Processus",
        tolerance1: "Tolérance 1",
        tolerance2: "Tolérance 2",
        totalStock: "Stock total",
        quantityUOM: "Quantité UOM",
        deliveryDate: "La date de livraison",
        weight: "Poids (lb)",
        netPrice: "Prix ​​net",
        priceUOM: "Prix UOM",
        extendedPrice: "Prix ​​prolongée",
        lineLevelPO: "Bon de commande au niveau de la ligne",
        requestFutureDeliveryDate: "Demander une date de livraison future",
        customerReferenceNumber: "Numéro de référence du client",
        lineItemNote: "Remarque sur l'élément de campagne",
        domesticRequested: "Domestique demandée",
        tbd: "à déterminer",
        seeAbove: "Voir au dessus",
        cutFrom: "Couper de: ",
    },
    es: {
        customerNumber: "Número de cliente",
        totalOrderWeight: "Peso total del pedido (LB)",
        subTotal: "Total parcial (est.)",
        orderSpecialCharges: "Cargos especiales por pedido",
        itemSpecialCharges: "Cargos especiales por artículo",
        totalExtendedPrice: "Total Precio extendido",
        taxes: "Impuestos (est.)",
        fuelSurcharge: "Recargo por combustible (est.)",
        energySurcharge: "Recargo de energia (est.)",
        discountApplied: "Descuento aplicado",
        customerPickup: "El cliente recoge",
        currency: "Divisa",
        totalNetPrice: "Precio neto total (est.)",
        timeStamp: "Marca de tiempo",
        purchaseOrderNumber: "Número de orden de compra",
        note: "Nota",
        noteText:
            "Las Condiciones y Términos de Venta de %company% se aplican a todas las ventas realizadas por %company%.",
        noteLinkTooltip: "Condiciones y términos de venta de %company%",
        partNumber: "Número de pieza",
        altType: "Tipo alternativo",
        alt: "Alt.",
        description: "Descripción",
        requestedQuantity: "Cantidad requerida",
        requestedUOM: "Solicitada UOM",
        cutDescription: "Descripción del corte",
        cut: "Cortar",
        cutDimension1: "Dimensión de corte 1",
        cutDimension2: "Dimensión de corte 2",
        cutShape: "Forma de corte",
        process: "Proceso",
        tolerance1: "Tolerancia 1",
        tolerance2: "Tolerancia 2",
        totalStock: "Existencias totales",
        quantityUOM: "Cantidad UOM",
        deliveryDate: "Fecha de entrega",
        weight: "Peso (libras)",
        netPrice: "Precio neto",
        priceUOM: "Precio UOM",
        extendedPrice: "Precio ampliado",
        lineLevelPO: "Orden de compra a nivel de línea",
        requestFutureDeliveryDate: "Solicitar fecha de entrega futura",
        customerReferenceNumber: "Número de referencia del cliente",
        lineItemNote: "Nota de línea de pedido",
        domesticRequested: "Nacional Solicitado",
        tbd: "por determinar",
        seeAbove: "Véase más arriba",
        cutFrom: "Cortar desde: ",
    },
};
